import { yupResolver } from "@hookform/resolvers/yup";
import { Flex, Form, Link, NumberedMilestones, Text } from "@wayflyer/flyui";
import { RhfTextField } from "@wayflyer/flyui-hook-form";
import type { ReactNode } from "react";
import { useForm } from "react-hook-form";
import type { MessageDescriptor } from "react-intl";
import { FormattedMessage, defineMessage, defineMessages, useIntl } from "react-intl";
import { useCustomerAppConfig } from "undercarriage";
import * as Yup from "yup";

import { AuthCallbackPath } from "../../callback-paths";
import { usePlatformConnectorProperties } from "../../queries";
import { connectPostPath, reconnectPostPath, useConnectionFlowsConfig } from "../../config";
import type { BigcommerceOtpData } from "../../common/connect-by-post";
import { useConnectByPost } from "../../common/connect-by-post";
import { ConnectionsList } from "../../common/connections";
import { OAuthErrorBanner } from "../../common/oauth-error-banner";
import { MESSAGES } from "../../messages";
import type { DataSourcesSchemas } from "api-types";

const DEFAULT_OTP_ERROR = defineMessage({
  id: "funding-application: valid bigcommerce otp hint",
  defaultMessage: "Please enter the OTP as shown in the Wayflyer BigCommerce app.",
});
const DEFAULT_TOKEN_ERROR = defineMessage({
  id: "funding-application: valid bigcommerce otp access token hint",
  defaultMessage: "We could not find a valid access token. Please contact support.",
});

const BIGCOMMERCE_SERVER_VALIDATION_ERRORS: Map<string, MessageDescriptor> = new Map([
  ["valid bigcommerce access token hint", DEFAULT_TOKEN_ERROR],
  ["valid bigcommerce otp hint", DEFAULT_OTP_ERROR],
]);

const CONNECTOR_SLUG = "bigcommercerutter";

const BIGCOMMERCE_APP_STORE_URL = "https://login.bigcommerce.com/deep-links/marketplace/apps/32561";

const BIGCOMMERCE_MESSAGES = defineMessages({
  bigcommerceConnectStepOneTitle: {
    id: "funding-application: bigcommerce connect step one title",
    defaultMessage: "Install the Wayflyer BigCommerce app",
  },
  bigcommerceConnectStepOneDescription: {
    id: "funding-application: bigcommerce connect step one description",
    defaultMessage: "<p><a>Click here</a> to go to the BigCommerce app store, and install the Wayflyer app.</p>",
  },
  bigcommerceConnectStepTwoTitle: {
    id: "funding-application: bigcommerce connect step two title",
    defaultMessage: "Enter the code shown at BigCommerce into the box below",
  },
  bigcommerceConnectStepTwoDescription: {
    id: "funding-application: bigcommerce connect step two description",
    defaultMessage: "You will see a code in the Wayflyer BigCommerce app. Copy and paste it here.",
  },
});

const BigcommerceConnectForm = ({
  targetPath,
  onInitiateConnection,
}: {
  targetPath: string | null | undefined;
  onInitiateConnection?: () => void;
}) => {
  const intl = useIntl();
  const connectByPost = useConnectByPost();

  const schema = Yup.object({
    otp: Yup.string()
      .trim()
      .required(intl.formatMessage(DEFAULT_OTP_ERROR))
      .matches(/^[a-zA-Z0-9]{24}$/, intl.formatMessage(DEFAULT_OTP_ERROR)),
  });

  const bigCommerceForm = useForm({
    resolver: yupResolver(schema),
  });

  const connectHandler = async (formData: Yup.InferType<typeof schema>) => {
    if (targetPath) {
      onInitiateConnection?.();
      const postData: BigcommerceOtpData = { otp: formData.otp };
      const response = await connectByPost(targetPath, postData);

      if (response?.errorMessage) {
        if (response.errorField === "bigcommerceAccessToken") {
          bigCommerceForm.clearErrors();
          bigCommerceForm.setError("root.bigcommerceAccessToken", {
            type: "validate",
            message: intl.formatMessage(
              BIGCOMMERCE_SERVER_VALIDATION_ERRORS.get(response.errorMessage) ?? DEFAULT_TOKEN_ERROR
            ),
          });
        }
        if (response.errorField === "otp") {
          bigCommerceForm.clearErrors();
          bigCommerceForm.setError("otp", {
            type: "validate",
            message: intl.formatMessage(
              BIGCOMMERCE_SERVER_VALIDATION_ERRORS.get(response.errorMessage) ?? DEFAULT_OTP_ERROR
            ),
          });
        }
      }
    }
  };

  const milestones = [
    {
      number: "01",
      title: intl.formatMessage(BIGCOMMERCE_MESSAGES.bigcommerceConnectStepOneTitle),
      body: intl.formatMessage(BIGCOMMERCE_MESSAGES.bigcommerceConnectStepOneDescription, {
        p: (chunks: UIElement[]) => <Text>{chunks}</Text>,
        a: (chunks: UIElement[]) => (
          <Link url={BIGCOMMERCE_APP_STORE_URL} external>
            {chunks}
          </Link>
        ),
      }),
    },
    {
      number: "02",
      title: intl.formatMessage(BIGCOMMERCE_MESSAGES.bigcommerceConnectStepTwoTitle),
      body: intl.formatMessage(BIGCOMMERCE_MESSAGES.bigcommerceConnectStepTwoDescription),
    },
  ];

  return (
    <Form
      onSubmit={bigCommerceForm.handleSubmit(connectHandler)}
      primaryActionVariant="Tertiary"
      primaryAction={{
        content: intl.formatMessage({
          id: "funding-application: bigcommerce log in button label",
          defaultMessage: "Log in to BigCommerce",
        }),
        disabled: bigCommerceForm.formState.isSubmitting,
        loading: bigCommerceForm.formState.isSubmitting,
      }}
    >
      <NumberedMilestones milestones={milestones} />
      <RhfTextField
        label={intl.formatMessage({
          id: "funding-application: bigcommerce enter otp",
          defaultMessage: "Enter the code displayed in the Wayflyer BigCommerce app",
        })}
        form={bigCommerceForm}
        disabled={bigCommerceForm.formState.isSubmitting}
        name="otp"
      />
      <FormattedMessage
        id="funding-application: codat third party disclaimer"
        defaultMessage="<p>We use a third party service called <a>Codat</a> to connect to and access your data</p>"
        values={{
          p: (chunks: UIElement[]) => <Text color="placeholder">{chunks}</Text>,
          a: (chunks: UIElement[]) => (
            <Link url="https://www.codat.io/powered-by-codat/" external>
              {chunks}
            </Link>
          ),
        }}
      />
    </Form>
  );
};

interface BigcommerceOtpConnectProps {
  postConnectPath: AuthCallbackPath;
  onInitiateConnection?: () => void;
}

export const BigcommerceConnectOtp = ({ postConnectPath, onInitiateConnection }: BigcommerceOtpConnectProps) => {
  const intl = useIntl();
  const {
    data: { customerDomain },
  } = useCustomerAppConfig();
  const { connectorsApiUrl } = useConnectionFlowsConfig();
  const { data: connectorProperties } = usePlatformConnectorProperties(
    customerDomain,
    CONNECTOR_SLUG,
    postConnectPath,
    connectorsApiUrl
  );

  return (
    <Flex direction="column" gap="8">
      <Flex direction="column" justify="flex-start">
        <Flex direction="column" gap="8">
          <OAuthErrorBanner connectorSlug={CONNECTOR_SLUG} />
          <Text>{intl.formatMessage(MESSAGES.ecommerceDataUsage, { platform: "BigCommerce" })}</Text>
          <ConnectionsList
            connectorProperties={connectorProperties}
            renderConnectAnother={(onConnectAnother) => (
              <Text size="sm">
                {intl.formatMessage(MESSAGES.connectAnotherStore, {
                  link: (chunks: ReactNode) => <Link onClick={onConnectAnother}>{chunks}</Link>,
                })}
              </Text>
            )}
            connectForm={
              <BigcommerceConnectForm
                targetPath={connectPostPath(customerDomain, CONNECTOR_SLUG, postConnectPath)}
                onInitiateConnection={onInitiateConnection}
              />
            }
            renderReconnectForm={(connection: DataSourcesSchemas.ConnectionPropertiesSchema) => (
              <BigcommerceConnectForm
                targetPath={reconnectPostPath(customerDomain, CONNECTOR_SLUG, connection.id, postConnectPath)}
                onInitiateConnection={onInitiateConnection}
              />
            )}
          />
        </Flex>
      </Flex>
    </Flex>
  );
};
